import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { initAnalytics, trackPageview } from "@/lib/analytics";
import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { BRAND, absoluteUrl } from "../lib/brand";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow mb-4">404</p>
        <h1 className="headline-lg">Page not found</h1>
        <p className="mt-4 text-foreground/90">
          The page you're looking for doesn't exist or has moved.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90"
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  const reportedError = error instanceof Error ? error : new Error(String(error));
  console.error(reportedError);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(reportedError, { boundary: "tanstack_root_error_component" });
  }, [reportedError]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="headline-md">This page didn't load</h1>
        <p className="mt-3 text-sm text-foreground/90">
          Something went wrong on our end.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="bg-primary px-5 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="border border-border px-5 py-3 text-xs uppercase tracking-[0.18em] text-foreground hover:bg-surface-2"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: BRAND.name,
  alternateName: "KCN",
  description: BRAND.description,
  slogan: "A path to wealth and freedom.",
  founder: {
    "@type": "Person",
    name: BRAND.founder,
    jobTitle: "Founder & CEO, Kristan Cole Network",
    affiliation: { "@type": "Organization", name: "Keller Williams Realty" },
    knowsAbout: [
      "Real estate team leadership",
      "Wealth building for agents",
      "Real estate expansion",
      "Agent coaching",
    ],
    award: [
      "Realtor of the Year",
      "2018 Inman Innovator Finalist",
    ],
  },
  parentOrganization: {
    "@type": "Organization",
    name: "Keller Williams Realty",
    url: "https://www.kw.com",
  },
  areaServed: BRAND.areas.map((a) => ({ "@type": "State", name: a })),
  url: "https://joinkcn.com/",
  logo: absoluteUrl(BRAND.logos.onWhite),
  image: absoluteUrl(BRAND.logos.onWhite),
  sameAs: [
    "https://www.facebook.com/KristanColeNetwork/",
    "https://www.linkedin.com/company/kristancolenetwork/",
    "https://www.youtube.com/@kristancolenetwork",
    "https://www.instagram.com/kristancolenetwork/",
  ],
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: BRAND.name,
  url: "https://joinkcn.com/",
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "k068qmP31q0lfVBriKeCIsSSm6r-negUdMQnzUIdEiU" },
      { title: "Kristan Cole Network | Join the Top Keller Williams Realty Team" },
      {
        name: "description",
        content:
          "Join the top Keller Williams real estate team. 40+ years of leadership, $1B+ closed in 2025, and the COLE Method that takes agents from producer to CEO.",
      },
      { name: "author", content: "Kristan Cole Network" },
      { name: "theme-color", content: "#000000" },
      { property: "og:site_name", content: BRAND.name },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Kristan Cole Network | Join the Top Keller Williams Realty Team" },
      {
        property: "og:description",
        content:
          "Join the top Keller Williams real estate team. 40+ years of leadership, $1B+ closed in 2025, and the COLE Method that takes agents from producer to CEO.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Kristan Cole Network | Join the Top Keller Williams Realty Team" },
      {
        name: "twitter:description",
        content:
          "Join the top Keller Williams real estate team. 40+ years of leadership, $1B+ closed in 2025, and the COLE Method that takes agents from producer to CEO.",
      },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/a1c81596-2b23-426e-9109-5a765558ab54/id-preview-ee71fd9a--06f75fc4-7c28-4545-a3c5-5aa4e0607bfa.lovable.app-1782419466698.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/a1c81596-2b23-426e-9109-5a765558ab54/id-preview-ee71fd9a--06f75fc4-7c28-4545-a3c5-5aa4e0607bfa.lovable.app-1782419466698.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "preconnect", href: "https://services.leadconnectorhq.com" },
      { rel: "preconnect", href: "https://links.northgeorgiapropertyexperts.com" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(orgJsonLd),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteJsonLd),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    initAnalytics();
    // Track a pageview on the initial load and every client-side navigation.
    trackPageview(router.state.location.pathname);
    const unsub = router.subscribe("onLoad", () => {
      trackPageview(router.state.location.pathname);
    });
    return unsub;
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
